import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import FieldToast from "../components/FieldToast";
import "./Settings.css";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import { updateProfilePicture, updatePassword, getEmployeeProfile } from "../api/api";

// ─── Initials Avatar ──────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#6366f1","#0ea5e9","#10b981","#f59e0b","#ec4899","#8b5cf6","#14b8a6",
];

function getAvatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function InitialsAvatar({ name, size = 100 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: getAvatarColor(name),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        fontWeight: 700,
        color: "#fff",
        letterSpacing: 1,
        flexShrink: 0,
        userSelect: "none",
      }}
    >
      {getInitials(name)}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Settings() {
  const { currentUser, syncUser, logout } = useAuth();
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [preview, setPreview] = useState(null); // null = no image → show initials

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const oldPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);

  const [fieldToast, setFieldToast] = useState({
    show: false,
    message: "",
    targetRef: null,
  });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({ show: true, message, targetRef });
    setTimeout(() => setFieldToast((prev) => ({ ...prev, show: false })), 3000);
  };

  // ── Load avatar from server ──────────────────────────────────────────────────
  useEffect(() => {
    const loadEmployeeProfile = async () => {
      if (!currentUser?._id) return;

      try {
        const profileData = await getEmployeeProfile(currentUser._id);
        const avatarUrl =
          profileData?.data?.avatar ||
          profileData?.data?.profilePicture?.url ||
          profileData?.data?.photo ||
          profileData?.avatar ||
          profileData?.profilePicture?.url ||
          profileData?.photo;

        if (avatarUrl) {
          const clean = avatarUrl.split("?")[0];
          setPreview(`${resolveImageUrl(clean)}?t=${Date.now()}`);
        } else {
          setPreview(null); // → will show initials
        }
      } catch (err) {
        console.error("Failed to load employee profile:", err);
        const avatarUrl =
          currentUser?.avatar || currentUser?.profilePicture?.url;
        if (avatarUrl) {
          const clean = avatarUrl.split("?")[0];
          setPreview(`${resolveImageUrl(clean)}?t=${Date.now()}`);
        } else {
          setPreview(null);
        }
      }
    };

    loadEmployeeProfile();
  }, [currentUser?._id]);

  // ── Cleanup blob URL ─────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setPreview(URL.createObjectURL(file));
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();

    try {
      if (!avatarFile && (typeof password !== "string" || !password.trim())) {
        showFieldToast("Nothing to update", newPasswordRef);
        return;
      }

      const userId = currentUser?._id || currentUser?.id;
      let updatedUser = { ...currentUser };
      let passwordUpdated = false;

      // avatar
      if (avatarFile) {
        const res = await updateProfilePicture(userId, avatarFile);
        console.log("Avatar upload response:", JSON.stringify(res, null, 2));

        const newAvatarUrl =
          res?.data?.profilePicture?.url ||
          res?.data?.avatar ||
          res?.data?.photo ||
          res?.profilePicture?.url ||
          res?.avatar ||
          res?.photo;

        if (newAvatarUrl) {
          const baseUrl = newAvatarUrl.split("?")[0];
          const bustedUrl = `${resolveImageUrl(baseUrl)}?t=${Date.now()}`;
          updatedUser = {
            ...updatedUser,
            avatar: bustedUrl,
            profilePicture: { url: bustedUrl },
            photo: bustedUrl,
          };
          setPreview(bustedUrl);
          toast.success("Avatar updated");
        } else {
          toast.error("Failed to update avatar – no URL returned from server");
        }
      }

      // password
      if (typeof password === "string" && password.trim()) {
        if (typeof oldPassword !== "string" || !oldPassword.trim()) {
          showFieldToast("Please enter your old password", oldPasswordRef);
          return;
        }
        await updatePassword(oldPassword, password);
        toast.success("Password updated. You will be logged out.");
        passwordUpdated = true;
      }

      syncUser(updatedUser);
      setAvatarFile(null);
      setOldPassword("");
      setPassword("");

      if (passwordUpdated) setTimeout(() => logout(), 1500);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Update failed");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  const userName = currentUser?.userName || currentUser?.name || "";

  return (
    <div className="settings-page">
      <h2 className="settings-title">Settings</h2>

      {/* Profile Picture */}
      <div className="settings-section">
        <h3>Profile Picture</h3>

        {/* ✅ Show image if available, otherwise show initials */}
        {preview ? (
          <img
            key={preview}
            src={preview}
            alt="avatar"
            className="avatar-img"
          />
        ) : (
          <InitialsAvatar name={userName} size={100} />
        )}

        <div className="avatar-actions">
          <label className="avatar-pick-btn">
            Choose Image
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="avatar-input"
            />
          </label>
          <span className="avatar-file-name" title={avatarFile?.name || ""}>
            {avatarFile?.name || "No file selected"}
          </span>
        </div>
      </div>

      {/* Password */}
      <div className="settings-section">
        <h3>Change Password</h3>
        <div className="password-input-wrapper">
          <input
            ref={oldPasswordRef}
            type={showOldPassword ? "text" : "password"}
            placeholder="Old Password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="password-input"
          />
          <button
            type="button"
            className="show-password-btn"
            onClick={() => setShowOldPassword(!showOldPassword)}
          >
            {showOldPassword ? "🙈" : "👁️"}
          </button>
        </div>
        <div className="password-input-wrapper">
          <input
            ref={newPasswordRef}
            type={showNewPassword ? "text" : "password"}
            placeholder="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="password-input"
          />
          <button
            type="button"
            className="show-password-btn"
            onClick={() => setShowNewPassword(!showNewPassword)}
          >
            {showNewPassword ? "🙈" : "👁️"}
          </button>
        </div>
      </div>

      {/* Save */}
      <button className="save-btn" onClick={handleSave}>
        Save Changes
      </button>

      <FieldToast
        message={fieldToast.message}
        targetRef={fieldToast.targetRef}
        show={fieldToast.show}
      />
    </div>
  );
}